import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Text,
  Skeleton,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SortUpIcon } from '../SortIcons/SortUpIcon';
import { SortDownIcon } from '../SortIcons/SortDownIcon';
import './AleaTable.scss';
import { ITableColumn } from '../../interfaces';
import getTagNameByLanguage from '../../utils/getTagName.ts';
import AleaPagination from '../AleaPagination/AleaPagination.tsx';
import { useTranslation } from 'react-i18next';

interface AleaTableProps<T> {
  data: T[];
  columns: ITableColumn<T>[];
  categories?: string[];
  onRowClick?: (item: T) => void;
  isLoading: boolean;
  total: number;
  expandable?: boolean;
  expandedRowRender?: (item: T) => React.ReactNode;
  noRecordsText?: string;
  /**
   * AleaTable historically applied `colSpan=2` to the last column cells.
   * Use this prop to override that behavior per-table when it causes unwanted extra width.
   *
   * @default 2
   */
  lastColumnColSpan?: 1 | 2;
  // Pagination props
  usePagination?: boolean;
  currentPage?: number;
  perPage?: number;
  onPageChange?: (page: number) => void;
  // External expanded row control
  externalExpandedRowId?: string | null;
  onPerPageChange?: (perPage: number) => void;
  // Padding control for first column when no expand icon
  firstColumnPadding?: boolean;
  // Optional active row highlight (e.g. for action menus)
  activeRowId?: string | null;
}

const AleaTable = <T extends Record<string, unknown>>({
  data,
  columns,
  categories,
  onRowClick,
  isLoading,
  total,
  expandable = false,
  expandedRowRender,
  noRecordsText = 'noRecordsFound',
  lastColumnColSpan = 2,
  // Pagination props
  usePagination = false,
  currentPage = 1,
  perPage = 10,
  onPageChange,
  // External expanded row control
  externalExpandedRowId,
  onPerPageChange,
  // Padding control for first column when no expand icon
  firstColumnPadding = true,
  // Optional active row highlight
  activeRowId = null,
}: AleaTableProps<T>) => {
  // i18n translation hook
  const { t } = useTranslation();

  // State for selected category filter
  const [selectedCategory, setSelectedCategory] = useState('All');

  // React Router hooks for navigating and getting the current location
  const location = useLocation();
  const navigate = useNavigate();

  // URLSearchParams for handling URL parameters (order by and sort order)
  const params = new URLSearchParams(location.search);

  const orderBy = (params.get('orderBy') as keyof T) || '';
  const sortOrder = params.get('sortOrder') || '';

  // State for storing expanded row ID
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  /**
   * Handles sorting of the table based on the specified key.
   * Toggles the sort direction between ascending and descending.
   * Updates the URL with the order by key and direction for sharing.
   *
   * @param {keyof T} key - The key to sort the data by.
   */
  const handleSort = (col: ITableColumn<T>) => {
    if (!col.sortable) return;

    if (col.onSort) {
      col.onSort();
    } else {
      let direction: 'asc' | 'desc' = 'asc';
      if (orderBy === col.key && sortOrder === 'asc') {
        direction = 'desc';
      }
      params.set('orderBy', col.key as string);
      params.set('sortOrder', direction);
      
      // Reset to page 1 when sorting if pagination is enabled
      if (usePagination) {
        params.set('page', '1');
      }
      
      navigate(`${location.pathname}?${params.toString()}`);
    }
  };

  const handleCategoryClick = (category: string) => {
    const newParams = new URLSearchParams(location.search);

    if (selectedCategory === category) {
      // Deselect the category if it's already selected
      setSelectedCategory('All');
      newParams.delete('tags');
    } else {
      setSelectedCategory(category);
      newParams.set('tags', category);
    }

    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  };

  const getCellColSpan = (colIndex: number) => {
    return colIndex === columns.length - 1 ? lastColumnColSpan : 1;
  };

  // Function to handle row click
  const handleRowClick = (item: T) => {
    if (expandable && externalExpandedRowId === undefined) {
      // Only manage internal state if no external state is provided
      setExpandedRowId(
        expandedRowId === String(item.id) ? null : String(item.id)
      );
    }
    // Call onRowClick prop if it exists
    onRowClick && onRowClick(item);
  };

  // Use external expanded row ID if provided, otherwise use internal state
  const currentExpandedRowId = externalExpandedRowId !== undefined ? externalExpandedRowId : expandedRowId;

  return (
    <Box>
      {/* categories */}
      <Flex>
        {categories &&
          categories.map((category) => (
            <Box
              key={category}
              py={3}
              px={4}
              borderRadius="20px"
              border="1px solid"
              borderColor={
                selectedCategory === category ? 'alea.500' : '#C6C6C6'
              }
              fontWeight={selectedCategory === category ? '500' : '400'}
              backgroundColor="white"
              color={selectedCategory === category ? 'alea.500' : '#C6C6C6'}
              cursor="pointer"
              mr={3}
              onClick={() => handleCategoryClick(category)}
            >
              {getTagNameByLanguage(category)}
            </Box>
          ))}
      </Flex>

      {/* table */}
      <Table variant="simple" className="alea-table">
        <Thead backgroundColor="#F3F2F2" position="relative">
          <Tr>
            {columns.map((col) => (
              <Th
                key={col.key as string}
                onClick={() => handleSort(col)}
                cursor={col.sortable ? 'pointer' : 'default'}
                sx={{ userSelect: 'none' }}
                width={col.width}
                p={col.headerPadding}
                data-expand-icon={col.key === 'expandIcon' ? 'true' : 'false'}
              >
                {isLoading ? (
                  <Flex align="center" justifyContent="flex-start">
                    <Skeleton height="20px" width="60%" />
                  </Flex>
                ) : (
                  <Flex align="center">
                    {col.sortable && (
                      <Flex direction="column" align="center" mr={2}>
                        <SortUpIcon
                          active={orderBy === col.key && sortOrder === 'asc'}
                        />
                        <SortDownIcon
                          active={orderBy === col.key && sortOrder === 'desc'}
                        />
                      </Flex>
                    )}
                    <Box>{col.label}</Box>
                  </Flex>
                )}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading ? (
            // Show skeleton rows when loading
            Array.from({ length: 5 }).map((_, index) => (
              <Tr key={`skeleton-${index}`} bg="white" borderBottom="1px solid #DCDCDC">
                {columns.map((col, colIndex) => (
                  <Td
                    key={col.key as string}
                    colSpan={getCellColSpan(colIndex)}
                    p={
                      col.cellPadding ??
                      (col.key === 'expandIcon'
                        ? 0
                        : !firstColumnPadding && colIndex === 0
                          ? 0
                          : undefined)
                    }
                  >
                    <Skeleton height="20px" width="80%" />
                  </Td>
                ))}
              </Tr>
            ))
          ) : (
            <>
              {data.filter(item => item && item.id).map((item, index) => {
                const isExpanded = currentExpandedRowId === String(item.id);
                const isActive = activeRowId === String(item.id);
                const isDimmed =
                  (currentExpandedRowId && !isExpanded) ||
                  (activeRowId && !isActive);

                return (
                <React.Fragment key={index}>
                  <Tr
                    onClick={() => handleRowClick(item)}
                    _hover={{
                      cursor: onRowClick || expandable ? 'pointer' : 'default',
                    }}
                    bg={
                      isExpanded || isActive
                        ? '#F8F8F8'
                        : 'white'
                    }
                    boxShadow={
                      isActive
                        ? '0px 8px 24px rgba(0,0,0,0.12)'
                        : 'none'
                    }
                    position={isActive ? 'relative' : 'static'}
                    zIndex={isActive ? 1501 : 'auto'}
                    borderBottom="1px solid #DCDCDC"
                    // Apply greying-out effect to non-expanded or non-active rows
                    opacity={isDimmed ? 0.4 : 1}
                    pointerEvents={isDimmed ? 'none' : 'auto'}
                    transition="opacity 0.2s ease-in-out"
                  >
                    {columns.map((col, colIndex) => (
                      <Td
                        key={col.key as string}
                        colSpan={getCellColSpan(colIndex)}
                        p={
                          col.cellPadding ??
                          (col.key === 'expandIcon'
                            ? 0
                            : !firstColumnPadding && colIndex === 0
                              ? 0
                              : undefined)
                        }
                        lineHeight="1.5"
                        verticalAlign="top"
                        data-expand-icon={col.key === 'expandIcon' ? 'true' : 'false'}
                        // Safari workaround: Apply position and z-index to Td as well
                        position={isActive ? 'relative' : undefined}
                        zIndex={isActive ? 1501 : undefined}
                        transform={isActive ? 'translate3d(0,0,0)' : undefined}
                        bg={isActive ? 'white' : 'inherit'}
                        // Apply greying effect to expanded row content (except expand icon)
                        opacity={
                          currentExpandedRowId === String(item.id) && col.key !== 'expandIcon'
                            ? 0.4
                            : 1
                        }
                      >
                        {col.render
                          ? col.render(item)
                          : (item[col.key] as React.ReactNode)}
                      </Td>
                    ))}
                  </Tr>
                  {expandable && currentExpandedRowId === String(item.id) && expandedRowRender && (
                    <Tr bg="#F8F8F8">
                      <Td colSpan={columns.length + 1} p={0}>
                        {expandedRowRender(item)}
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              )})}
              {data.length === 0 && !isLoading && (
                <Tr>
                  <Td colSpan={columns.length + 1} textAlign="center">
                    <Text width="100%" textAlign="center">{t(noRecordsText)}</Text>
                  </Td>
                </Tr>
              )}
            </>
          )}
        </Tbody>
      </Table>

      {/* Pagination */}
      {usePagination && (
        <Box>
          <AleaPagination
            currentPage={currentPage}
            totalPages={Math.ceil(total / perPage)}
            total={total}
            perPage={perPage}
            onPageChange={onPageChange!}
            onPerPageChange={onPerPageChange}
            isLoading={isLoading}
          />
        </Box>
      )}
    </Box>
  );
};

export default AleaTable;
